import { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";

const MAX_WIDTH_CLASS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

export default function AppModal({
  open,
  title,
  onClose,
  children,
  footer = null,
  preventClose = false,
  maxWidth = "md",
  className = "",
}) {
  const dialogRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => {
      if (!preventClose) onClose();
    };
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose, preventClose]);

  const maxWidthClass = MAX_WIDTH_CLASS[maxWidth] ?? MAX_WIDTH_CLASS.md;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className={[
        "fixed left-1/2 top-1/2 z-50 m-0 max-h-[min(90vh,calc(100vh-2rem))] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2",
        "overflow-y-auto rounded-xl border-0 bg-white p-5 shadow-card backdrop:bg-slate-900/40",
        maxWidthClass,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onCancel={(event) => {
        if (preventClose) event.preventDefault();
      }}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <h3 id={titleId} className="text-lg font-semibold text-text-900">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          disabled={preventClose}
          className="shrink-0 rounded p-1 text-text-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close dialog"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      {children}

      {footer ? <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div> : null}
    </dialog>
  );
}

AppModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node,
  preventClose: PropTypes.bool,
  maxWidth: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  className: PropTypes.string,
};
